import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  TextField,
  Box,
  CircularProgress,
  Typography,
  IconButton,
  Tabs,
  Tab,
  Alert
} from '@mui/material';
import { adminApi } from '../../redux/adminApiSlice';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import RefreshIcon from '@mui/icons-material/Refresh';
import type { Neighborhood } from '../../types/interfaces';

interface Props {
  open?: boolean;
  onClose?: () => void;
  selectedNeighborhoods: Neighborhood[];
  onSelectionChange: (neighborhoods: Neighborhood[]) => void;
  disabled?: boolean;
}

const STORAGE_KEY = 'mockPostNeighborhoods';
const FAVORITES_KEY = 'favoriteNeighborhoods';

const NeighborhoodSelector: React.FC<Props> = ({
  open,
  onClose,
  selectedNeighborhoods,
  onSelectionChange,
  disabled
}) => {
  const { data: neighborhoods, isLoading, isError, error, refetch: refetchNeighborhoods } = adminApi.useGetMockNeighborhoodsQuery(undefined, {
    refetchOnMountOrArgChange: true
  });
  const { data: locationCounts } = adminApi.useGetLocationCountsQuery();
  const { data: allLocations } = adminApi.useGetAllLocationsQuery();
  const [searchTerm, setSearchTerm] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [favorites, setFavorites] = useState<Neighborhood[]>([]);
  const [showDebugInfo, setShowDebugInfo] = useState(false);
  const [localNeighborhoods, setLocalNeighborhoods] = useState<Neighborhood[]>([]);

  // Log neighborhood data for debugging
  useEffect(() => {
    if (neighborhoods) {
      console.log('Loaded neighborhoods from mock neighborhoods query:', neighborhoods);
    }
    if (isError) {
      console.error('Error loading neighborhoods:', error);
    }
  }, [neighborhoods, isError, error]);

  // Extract neighborhoods from all locations
  useEffect(() => {
    if (allLocations) {
      console.log('All locations loaded, filtering for neighborhoods...');
      try {
        // Filter for neighborhoods and transform to Neighborhood type
        const extractedNeighborhoods = allLocations
          .filter(location => location.level === 'NEIGHBORHOOD' || location.level === 'Neighborhood')
          .map(location => {
            // Find parent city if available
            const parentCity = allLocations.find(loc => 
              loc._id === (typeof location.parent === 'object' ? location.parent?._id : location.parent)
            );
            
            return {
              _id: location._id,
              name: location.name,
              cityName: parentCity?.name || 'Unknown City',
              level: 'NEIGHBORHOOD' as any,
              coordinates: location.coordinates
            };
          });
        
        console.log(`Found ${extractedNeighborhoods.length} neighborhoods from locations data`);
        
        if (extractedNeighborhoods.length > 0) {
          setLocalNeighborhoods(extractedNeighborhoods);
        }
      } catch (err) {
        console.error('Error processing all locations data:', err);
      }
    }
  }, [allLocations]);

  // Log location counts for debugging
  useEffect(() => {
    if (locationCounts) {
      console.log('Location counts:', locationCounts);
    }
  }, [locationCounts]);

  // Load persisted selections and favorites on mount
  useEffect(() => {
    const savedSelections = localStorage.getItem(STORAGE_KEY);
    if (savedSelections && selectedNeighborhoods.length === 0) {
      const parsed = JSON.parse(savedSelections);
      onSelectionChange(parsed);
    }

    const savedFavorites = localStorage.getItem(FAVORITES_KEY);
    if (savedFavorites) {
      setFavorites(JSON.parse(savedFavorites));
    }
  }, [onSelectionChange, selectedNeighborhoods.length]);

  // Save selections when they change
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(selectedNeighborhoods));
  }, [selectedNeighborhoods]);

  const handleToggle = (neighborhood: Neighborhood) => () => {
    const currentIndex = selectedNeighborhoods.findIndex(n => n._id === neighborhood._id);
    const newSelected = [...selectedNeighborhoods];

    if (currentIndex === -1) {
      newSelected.push(neighborhood);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    onSelectionChange(newSelected);
  };

  const handleRemoveSelected = (neighborhoodToRemove: Neighborhood) => {
    onSelectionChange(selectedNeighborhoods.filter(n => n._id !== neighborhoodToRemove._id));
  };

  const handleClearAll = () => {
    onSelectionChange([]);
  };

  const toggleFavorite = (neighborhood: Neighborhood) => {
    const newFavorites = favorites.some(f => f._id === neighborhood._id)
      ? favorites.filter(f => f._id !== neighborhood._id)
      : [...favorites, neighborhood];
    
    setFavorites(newFavorites);
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(newFavorites));
  };

  const isFavorite = (neighborhood: Neighborhood) => 
    favorites.some(f => f._id === neighborhood._id);

  // Get the neighborhoods to display based on current tab
  const getDisplayNeighborhoods = () => {
    // Use neighborhoods from the mock query, or if empty, use extracted ones from locations
    let availableNeighborhoods = localNeighborhoods;
    
    if (neighborhoods && Array.isArray(neighborhoods) && neighborhoods.length > 0) {
      availableNeighborhoods = neighborhoods;
    }
    
    if (!availableNeighborhoods || !Array.isArray(availableNeighborhoods)) {
      return [];
    }
    
    const sourceList = currentTab === 0 ? availableNeighborhoods : favorites;
    
    if (!searchTerm) {
      return sourceList;
    }
    
    return sourceList.filter(n => 
      (n.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
      (n.cityName?.toLowerCase() || '').includes(searchTerm.toLowerCase())
    );
  };

  const displayNeighborhoods = getDisplayNeighborhoods();
  const hasNeighborhoods = displayNeighborhoods.length > 0 || (neighborhoods && neighborhoods.length > 0) || localNeighborhoods.length > 0;

  return (
    <Dialog 
      open={open ?? false} 
      onClose={onClose ?? (() => {})} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          height: '80vh',
          maxHeight: '800px'
        }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Select Neighborhoods</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button 
              size="small" 
              color="primary" 
              variant="outlined" 
              onClick={() => refetchNeighborhoods()}
              startIcon={<RefreshIcon />}
            >
              Refresh
            </Button>
            <Button 
              size="small" 
              color="info" 
              variant="outlined" 
              onClick={() => setShowDebugInfo(!showDebugInfo)}
            >
              {showDebugInfo ? 'Hide Debug' : 'Debug'}
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {showDebugInfo && (
          <Alert severity="info" sx={{ m: 2 }}>
            <Typography variant="subtitle2">Available Neighborhood Sources:</Typography>
            <Typography variant="body2">Mock API: {neighborhoods?.length || 0} neighborhoods</Typography>
            <Typography variant="body2">Locations API: {localNeighborhoods.length} neighborhoods</Typography>
            {locationCounts && (
              <>
                <Typography variant="subtitle2" sx={{ mt: 1 }}>Database Location Counts:</Typography>
                <Typography variant="body2">Neighborhoods: {locationCounts.NEIGHBORHOOD}</Typography>
                <Typography variant="body2">Cities: {locationCounts.CITY}</Typography>
                <Typography variant="body2">States: {locationCounts.STATE}</Typography>
                <Typography variant="body2">Countries: {locationCounts.COUNTRY}</Typography>
                <Typography variant="body2">Total: {locationCounts.TOTAL}</Typography>
              </>
            )}
          </Alert>
        )}

        <Box sx={{ display: 'flex', height: '100%' }}>
          {/* Left side - Selection list */}
          <Box sx={{ 
            width: '60%', 
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Box sx={{ p: 2 }}>
              <TextField
                fullWidth
                placeholder="Search by neighborhood or city name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 1 }} />,
                }}
              />
            </Box>

            <Tabs 
              value={currentTab} 
              onChange={(_, newValue) => setCurrentTab(newValue)}
              sx={{ px: 2 }}
            >
              <Tab label="All" />
              <Tab label="Favorites" />
            </Tabs>

            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              px: 2
            }}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                  <CircularProgress />
                </Box>
              ) : !hasNeighborhoods ? (
                <Alert severity="info">No neighborhoods found.</Alert>
              ) : (
                <List dense>
                  {displayNeighborhoods.map((neighborhood) => {
                    const isSelected = selectedNeighborhoods.some(n => n._id === neighborhood._id);
                    return (
                      <ListItem 
                        key={neighborhood._id} 
                        dense 
                        button 
                        onClick={handleToggle(neighborhood)}
                        secondaryAction={
                          <IconButton 
                            edge="end" 
                            aria-label="toggle favorite" 
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleFavorite(neighborhood);
                            }}
                          >
                            {isFavorite(neighborhood) ? <StarIcon color="warning" /> : <StarBorderIcon />}
                          </IconButton>
                        }
                      >
                        <ListItemIcon>
                          <Checkbox
                            edge="start"
                            checked={isSelected}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={neighborhood.name}
                          secondary={neighborhood.cityName}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>

          {/* Right side - Selected items */}
          <Box sx={{ 
            width: '40%',
            display: 'flex',
            flexDirection: 'column',
            p: 2
          }}>
            <Typography variant="subtitle1" gutterBottom>
              Selected Neighborhoods ({selectedNeighborhoods.length})
            </Typography>
            <Box sx={{ 
              flex: 1, 
              overflow: 'auto',
              bgcolor: 'background.default',
              borderRadius: 1,
              p: 1
            }}>
              {selectedNeighborhoods.length === 0 ? (
                <Typography color="text.secondary" align="center" sx={{ mt: 2 }}>
                  No neighborhoods selected
                </Typography>
              ) : (
                <List dense>
                  {selectedNeighborhoods.map((neighborhood) => (
                    <ListItem
                      key={neighborhood._id}
                      secondaryAction={
                        <IconButton 
                          edge="end" 
                          aria-label="remove"
                          onClick={() => handleRemoveSelected(neighborhood)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    >
                      <ListItemText 
                        primary={neighborhood.name}
                        secondary={neighborhood.cityName}
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClearAll} color="error">
          Clear All
        </Button>
        <Button onClick={onClose} color="primary">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NeighborhoodSelector; 